import type { CityConfig } from "../city-template/types";

export const shelbyvilleConfig: CityConfig = {
  city: "Shelbyville",
  state: "Kentucky",
  stateAbbr: "KY",
  slug: "shelbyville",
  route: "/cities/shelbyville",
  title: "Emergency Passport Shelbyville KY | Expedited Passport Renewal | Shelby County",
  metaDescription: "Emergency passport and expedited passport services in Shelbyville, KY — as fast as 24 hours. Fast passport renewal for Shelby County, Simpsonville, and the Louisville East corridor. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Shelbyville, KY",
  heroSubtitle: "Need an emergency passport fast from Shelby County? Fast Passport Center helps Shelbyville and Louisville East corridor travelers get expedited passports as quickly as 24 hours. Serving the Shelby County seat, Simpsonville, and the eastern Jefferson County communities. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Shelbyville Kentucky historic downtown Main Street with charming small town architecture, clean Victorian-style buildings and tree-lined streets, wide panoramic view of Shelby County Kentucky county seat, bright daytime photography, no visible text or people",
  heroImageAlt: "Shelbyville Kentucky historic downtown Main Street with charming small town architecture",
  geoPosition: "38.2120;-85.2236",
  geoPlacename: "Shelbyville, Kentucky",
  lastModified: "2026-05-29",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "77 W Jackson Blvd, Suite 1200, Chicago, IL 60604",
    distance: "Located in downtown Chicago — approximately 340 miles from Shelbyville via I-65",
  },
  nearbyCitiesZips: [
    { name: "Shelbyville", zips: "40065, 40066" },
    { name: "Simpsonville", zips: "40067" },
    { name: "Eminence", zips: "40019" },
    { name: "Bagdad", zips: "40003" },
    { name: "Harrisonville", zips: "40065" },
    { name: "Todds Point", zips: "40065" },
    { name: "Frankfort (nearby)", zips: "40601, 40602" },
    { name: "Versailles (nearby)", zips: "40383, 40384, 40386" },
    { name: "Georgetown (nearby)", zips: "40324" },
    { name: "Taylorsville", zips: "40071" },
    { name: "Bardstown (nearby)", zips: "40004, 40013" },
    { name: "Louisville (nearby)", zips: "40207, 40223, 40241, 40299" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier for urgent Shelby County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Shelbyville case" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels from any UPS in Shelby County" },
  ],
  internalLinks: [
    { name: "Emergency passport services in Louisville", route: "/cities/louisville" },
    { name: "Expedited passport renewal in Frankfort", route: "/cities/frankfort" },
    { name: "Fast passport services in Georgetown", route: "/cities/georgetown" },
    { name: "Same-day passport help in Bardstown", route: "/cities/bardstown" },
    { name: "Urgent passport services in Lexington", route: "/cities/lexington" },
    { name: "Emergency passport help in Versailles", route: "/cities/versailles" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Shelbyville remotely via secure courier. We do not have a walk-in office in Shelby County — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Shelby County travelers.",
  fpcOfficeDistance: "Serving all of Shelbyville, Simpsonville, and the Shelby County corridor",
  serviceAreaIntro: "From the historic Shelbyville Main Street to Simpsonville, from Eminence to Taylorsville — Fast Passport Center serves the entire Shelby County and Louisville East corridor area.",
  nearbyCitiesIntro: "We serve all Shelbyville neighborhoods and nearby communities including Simpsonville, Eminence, Bagdad, Harrisonville, Todds Point, and nearby Frankfort, Versailles, Georgetown, Taylorsville, Bardstown, and Louisville. If you are in Shelby County or the surrounding region, we have you covered.",
  howItWorksIntro: "Most Shelbyville travelers complete everything online and ship documents overnight from the nearest UPS. No need to drive to Chicago or wait in line at the regional agency.",
  mistakesIntro: "Shelby County travelers often underestimate how long passport processing takes. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Shelbyville and Shelby County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Shelbyville? Here are the most common ones we hear from Shelby County travelers.",
  statePageRoute: "/states/kentucky",
  statePageName: "Kentucky",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Medical crisis documentation required",
        "Long drive to Chicago — over 5 hours from Shelbyville",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission slots",
        "Predictable turnaround for Shelby County travelers",
        "Ideal for upcoming business or leisure travel",
        "Full document review included",
        "Dedicated agent assigned to your Shelbyville case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service mail options",
        "Good for planned travel in the next few months",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Cannot upgrade speed after submission",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Shelby County travelers with immediate departure needs — as fast as 24 hours for urgent Louisville East corridor international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Shelbyville adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with local acceptance clerk support for Shelbyville applicants.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency replacement for Shelbyville residents with DS-64 form guidance and police documentation support.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors with parental consent and acceptance clerk support near Shelbyville.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes for Shelbyville residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Shelbyville", description: "Urgent 24-hour passport processing for Shelbyville and Shelby County travelers with immediate international departure needs." },
    { name: "Passport Renewal Shelbyville", description: "Fast passport renewal for Shelbyville residents with passport issued in last 15 years at age 16 or older." },
    { name: "New Passport Shelbyville", description: "First-time passport application assistance for Shelbyville and Shelby County residents." },
    { name: "Lost Passport Replacement Shelbyville", description: "Emergency passport replacement for lost or stolen passports in Shelby County." },
    { name: "Child Passport Shelbyville", description: "Passport services for minors in Shelbyville with parental consent and acceptance clerk support." },
    { name: "Name Change Passport Shelbyville", description: "Passport name change after marriage, divorce, or legal name changes for Shelbyville residents." },
  ],
  testimonials: [
    {
      name: "Jennifer M.",
      location: "Shelbyville, KY",
      rating: 5,
      text: "My husband's passport expired right before our anniversary trip to Ireland. I didn't think we could get it renewed from Shelbyville in time. Fast Passport Center handled it in 4 days. We had an amazing trip to Dublin. Thank you!",
      service: "Passport Renewal",
    },
    {
      name: "Mark T.",
      location: "Simpsonville, KY",
      rating: 5,
      text: "Business trip to Toronto in a week and my passport was missing. Fast Passport Center found a lost passport replacement in 36 hours. Professional service from start to finish. Highly recommend for Shelby County business travelers.",
      service: "Lost Passport",
    },
    {
      name: "Angela B.",
      location: "Shelbyville, KY",
      rating: 5,
      text: "Our daughter needed a passport for a school trip to France. Fast Passport Center helped us get her first passport in 5 days. The customer service was excellent — they answered every question we had.",
      service: "New Passport",
    },
    {
      name: "William S.",
      location: "Eminence, KY",
      rating: 5,
      text: "Emergency family situation in Mexico required travel within 5 days. My passport was expired. Fast Passport Center renewed it in 48 hours and I was on my flight. Life-saving service for Shelby County residents.",
      service: "Passport Renewal",
    },
  ],
  facilities: [
    {
      id: "shelbyville-1",
      name: "Shelbyville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "201 Main St",
      city: "Shelbyville",
      state: "KY",
      zip: "40065",
      phone: "(502) 633-1431",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Main post office for Shelby County residents",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Shelbyville location. Appointments strongly recommended. Full passport acceptance services.",
      icon: "ri-mail-line",
    },
    {
      id: "shelbyville-2",
      name: "Simpsonville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "6814 Taylorsville Rd",
      city: "Simpsonville",
      state: "KY",
      zip: "40067",
      phone: "(502) 722-1431",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 8 miles from Shelbyville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Simpsonville and surrounding Shelby County area. Good alternative for Shelbyville residents.",
      icon: "ri-mail-line",
    },
    {
      id: "shelbyville-3",
      name: "Frankfort Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1253 US-127",
      city: "Frankfort",
      state: "KY",
      zip: "40601",
      phone: "(502) 875-1431",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 22 miles from Shelbyville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Frankfort alternative for Shelby County residents. Major facility for the state capital area.",
      icon: "ri-mail-line",
    },
    {
      id: "shelbyville-4",
      name: "Jeffersontown Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "10420 Watterson Trail",
      city: "Jeffersontown",
      state: "KY",
      zip: "40299",
      phone: "(502) 266-1511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 28 miles from Shelbyville via I-64",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Louisville East area alternative for Shelbyville residents traveling to Louisville.",
      icon: "ri-mail-line",
    },
    {
      id: "shelbyville-5",
      name: "Bardstown Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "101 S 5th St",
      city: "Bardstown",
      state: "KY",
      zip: "40004",
      phone: "(502) 348-1431",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 25 miles from Shelbyville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Bardstown area alternative for Shelby County residents. Full passport services.",
      icon: "ri-mail-line",
    },
    {
      id: "shelbyville-6",
      name: "Shelby County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "501 Main St",
      city: "Shelbyville",
      state: "KY",
      zip: "40065",
      phone: "(502) 633-2331",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "Downtown Shelbyville",
      website: "https://www.shelbycountyclerk.com/",
      websiteLabel: "Shelby County Clerk",
      notes: "Full-service county clerk for Shelby County. Can process passport acceptance applications.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited", fpc: "No — we handle everything" },
    { feature: "Processing Speed", agency: "Same-day only for emergencies", fpc: "24–48 hours for emergency" },
    { feature: "Document Review", agency: "No — rejections sent back", fpc: "Yes — full review before submission" },
    { feature: "Travel Required", agency: "Yes — long drive to Chicago", fpc: "No — ship from home or Shelby County" },
    { feature: "Agent Support", agency: "Government clerk, no follow-up", fpc: "Yes — dedicated agent" },
    { feature: "Status Updates", agency: "No tracking provided", fpc: "Daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Incorrect Photo Dimensions",
      description: "Many Shelby County photo shops don't know the exact 2×2 inch passport requirement. Photos must have a plain white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Pre-Signed DS-11 Form",
      description: "The top reason for rejection at Shelby County acceptance facilities. The clerk must witness you sign the DS-11 in person.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Fee Payment",
      description: "Passport fees change frequently. Shelbyville applicants often arrive with outdated checks. We provide the exact current fee breakdown.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Travel Documentation",
      description: "Without confirmed flight details, the Chicago agency won't grant an emergency appointment. Always have your booking ready.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "Both parents must appear with a minor or submit notarized DS-3053. Many Shelby County families get caught off guard.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long",
      description: "Standard processing takes 8–11 weeks. Many Shelby County travelers assume they have time. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes from anywhere in Shelbyville or Shelby County." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We email you a prepaid overnight label. Drop your documents at any UPS location in Shelby County." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the U.S. Department of State." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a State Department facility using our registered courier slots — no postal delays." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport to your Shelbyville or Shelby County address — fully trackable." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Shelbyville, KY?", a: "Yes. Fast Passport Center provides emergency passport services for Shelbyville and Shelby County travelers. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the long drive to Chicago." },
    { q: "How fast can I renew my passport in Shelbyville?", a: "With Fast Passport Center, Shelbyville travelers can get a passport renewed in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day option is the most popular. Both options include document review and a dedicated agent." },
    { q: "Where is the nearest passport acceptance facility to Shelbyville?", a: "The most convenient options for Shelbyville residents are the Shelbyville Post Office (201 Main St), the Simpsonville Post Office (6814 Taylorsville Rd), and the Shelby County Clerk (501 Main St). All require appointments — we can help you find availability." },
    { q: "Do I need to drive to Chicago for an expedited passport from Shelbyville?", a: "No. With Fast Passport Center, you complete everything online and ship your documents via prepaid overnight label. Our registered couriers handle the in-person delivery — you stay in Shelby County and we do the rest." },
    { q: "Can I get a same-day passport from Shelbyville?", a: "Same-day passports from the Chicago Regional Passport Agency are only for life-or-death emergencies — and require proof of travel within 72 hours plus a long drive. For nearly all Shelbyville urgent travel needs, our 24–48 hour emergency courier service is the best option." },
    { q: "How do I get a child passport in Shelbyville, KY?", a: "For child passports in Shelbyville, both parents must appear at an acceptance facility or submit notarized DS-3053 consent. We walk Shelby County families through the entire process, including appointment scheduling." },
    { q: "What if I lost my passport before a trip from Shelbyville?", a: "Contact Fast Passport Center immediately. We provide lost passport replacement services for Shelbyville residents with DS-64 form guidance and emergency courier processing. We can get your replacement in 24–48 hours." },
    { q: "How much does expedited passport service cost in Shelbyville?", a: "Government fees are fixed: $130 for an adult passport book plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed. Contact us for a personalized quote based on your timeline." },
    { q: "What documents do I need for a first-time passport in Shelbyville?", a: "You need proof of citizenship (birth certificate), government-issued photo ID, two passport photos, and payment. First-time applicants must visit an acceptance clerk in person. We provide a full checklist and review your documents beforehand." },
    { q: "Is there a passport photo service near Shelbyville?", a: "Yes — CVS and Walgreens in Shelbyville, and Walmart on Taylorsville Road offer passport photos. We review your photos before submission to ensure they meet State Department standards." },
    { q: "Can Shelbyville business travelers use your service?", a: "Absolutely. Many Shelby County residents work along the I-64 corridor and travel internationally for business. We specialize in fast turnaround that accommodates professional schedules — complete everything online, ship overnight, receive your passport at your door." },
    { q: "How long does passport renewal take for Shelbyville residents?", a: "With Fast Passport Center, Shelbyville passport renewal takes 3–5 business days with our standard expedited option, or 24–48 hours with emergency processing. Standard government mail takes 8–11 weeks — not reliable for most Shelby County travel timelines." },
    { q: "What is the fastest passport service in Shelbyville, KY?", a: "The fastest option is Fast Passport Center's 24–48 hour emergency courier service. We hand-deliver your documents directly to a U.S. Department of State processing facility, bypassing mail queues and postal delays." },
    { q: "Can I use your service if my passport expires soon before a cruise?", a: "Yes. If your passport is expiring soon, contact us immediately. Cruise lines require your passport to be valid at least 6 months past your return date. We can expedite renewal for Shelbyville residents in 24–48 hours." },
    { q: "What areas near Shelbyville do you serve?", a: "We serve all of Shelbyville and the surrounding Shelby County communities including Simpsonville, Eminence, Bagdad, Harrisonville, Todds Point, and nearby Frankfort, Versailles, Georgetown, Taylorsville, Bardstown, and Louisville East." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Shelbyville", address: "1510 Midland Blvd, Shelbyville, KY 40065", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Shelbyville", address: "311 Main St, Shelbyville, KY 40065", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walmart — Shelbyville", address: "500 Taylorsville Rd, Shelbyville, KY 40065", hours: "Mon–Sun: 6AM–11PM" },
    { name: "CVS Pharmacy — Simpsonville", address: "1280 Simpsonville Rd, Simpsonville, KY 40067", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Frankfort", address: "1305 US-127, Frankfort, KY 40601", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walmart — Frankfort", address: "301 Leonardwood Dr, Frankfort, KY 40601", hours: "Mon–Sun: 6AM–11PM" },
  ],
  dmvLocations: [
    {
      name: "Shelby County Circuit Court Clerk",
      address: "501 Main St, Shelbyville, KY 40065",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Main clerk for Shelby County. Handles REAL ID and driver's license renewals. Appointment recommended.",
      featured: true,
    },
    {
      name: "Franklin County Circuit Court Clerk",
      address: "315 W Main St, Frankfort, KY 40601",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Frankfort alternative for Shelby County residents on the western side of the county.",
      featured: true,
    },
    {
      name: "Jefferson County Clerk — Main Office",
      address: "527 W Jefferson St, Louisville, KY 40202",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Louisville office for Shelby County residents traveling to the city.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "SDF",
      name: "Louisville Muhammad Ali International",
      distance: "35 miles from Shelbyville",
      address: "600 Terminal Dr, Louisville, KY 40209",
      note: "The primary airport for Shelby County travelers. Domestic flights with connections to major international hubs. Approximately 40 minutes from Shelbyville via I-64.",
      flightUrl: "https://www.flylouisville.com/",
      flightLabel: "Louisville Airport",
      featured: true,
    },
    {
      code: "LEX",
      name: "Blue Grass Airport (Lexington)",
      distance: "60 miles from Shelbyville",
      address: "4000 Terminal Dr, Lexington, KY 40510",
      note: "Alternative regional airport about 1 hour from Shelbyville via I-64.",
      flightUrl: "https://www.bluegrassairport.com/",
      flightLabel: "Blue Grass Airport",
      featured: false,
    },
    {
      code: "CVG",
      name: "Cincinnati/Northern Kentucky International",
      distance: "125 miles from Shelbyville",
      address: "3087 Terminal Dr, Hebron, KY 41048",
      note: "Major international hub for long-haul flights. Accessible via I-71 from Shelbyville.",
      flightUrl: "https://www.cvgairport.com/",
      flightLabel: "CVG Airport",
      featured: false,
    },
  ],
};